import { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft, Calendar, Clock, MapPin, Phone,
  Stethoscope, UserRound, Home, Heart, MessageCircle,
  CheckCircle, X, Star, ChevronDown, ChevronUp,
  Shield, AlertTriangle, FileText, Award, BookOpen, Languages, Users,
  Download,
} from 'lucide-react'
import { appointmentService, AppointmentMedicalInfo, VisitResult } from '@lib/supabase/appointment.service'
import { providerService, Provider } from '@lib/supabase/provider.service'
import { reviewService } from '@lib/supabase/review.service'
import { clsx } from 'clsx'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import Button from '@components/web/Button'
import ConfirmationModal from '@components/web/ConfirmationModal'
import StatusModal from '../../components/web/StatusModal'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { queryKeys } from '@lib/queryKeys'
import { downloadVisitResultPdf } from '@lib/visitResultPdf'

// ---- helpers ----------------------------------------------------------------
const DERMATOLOGY_ICON = '/dermatology.png'

const SERVICE_META: Record<string, { label: string; textColor: string; bg: string; icon: any }> = {
  'Doctor Visit':      { label: 'Doctor Visit',      textColor: 'text-blue-600',    bg: 'bg-blue-50',    icon: Stethoscope },
  'Nurse Care':        { label: 'Nurse Care',        textColor: 'text-emerald-600', bg: 'bg-emerald-50', icon: UserRound },
  'Home Care':         { label: 'Home Care',         textColor: 'text-purple-600',  bg: 'bg-purple-50',  icon: Home },
  'Specialized Care':  { label: 'Specialized Care',  textColor: 'text-rose-600',    bg: 'bg-rose-50',    icon: Heart },
  Dermatology:        { label: 'Dermatology',        textColor: 'text-teal-600',    bg: 'bg-teal-50',    icon: 'dermatology' },
  'Chat Consultation': { label: 'Chat Consultation', textColor: 'text-sky-600',     bg: 'bg-sky-50',     icon: MessageCircle },
}

const STATUS_STYLE: Record<string, string> = {
  scheduled: 'bg-green-100 text-green-700',
  requested: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-indigo-100 text-indigo-700',
  cancelled: 'bg-red-100 text-red-700',
  rejected:  'bg-red-100 text-red-700',
}

const URGENCY_LABEL: Record<string, { label: string; color: string }> = {
  normal: { label: 'Normal',  color: 'text-blue-600'   },
  high:   { label: 'High',    color: 'text-orange-600' },
  urgent: { label: 'Urgent',  color: 'text-red-600'    },
}

function formatTime12h(t?: string | null) {
  if (!t || t === '—') return '—'
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`
}

function getInitials(name: string) {
  return name.trim().split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', maximumFractionDigits: 0,
  }).format(n)
}

function normalizeStatus(status?: string | null) {
  return (status || '').toLowerCase().trim()
}

// ---- Detail Row -------------------------------------------------------------
function DetailRow({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  const isDermatologyIcon = Icon === 'dermatology'

  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
        {isDermatologyIcon ? (
          <img src={DERMATOLOGY_ICON} alt="" aria-hidden="true" className="h-4 w-4 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
        ) : Icon ? (
          <Icon size={15} className="text-muted-foreground" />
        ) : null}
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground mt-0.5">{value}</p>
      </div>
    </div>
  )
}

// ---- Review Modal -----------------------------------------------------------
function ReviewModal({
  providerName,
  onSubmit,
  onClose,
  submitError,
}: {
  providerName: string
  onSubmit: (rating: number, comment: string) => Promise<void>
  onClose: () => void
  submitError?: string | null
}) {
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent']

  const handleSubmit = async () => {
    if (rating === 0) return
    setIsSubmitting(true)
    await onSubmit(rating, comment)
    setIsSubmitting(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-none p-6 w-full max-w-sm shadow-xl">
        <h3 className="text-lg font-bold text-foreground text-center">Rate Your Experience</h3>
        <p className="text-sm text-muted-foreground text-center mt-1">How was your experience with {providerName}?</p>

        <div className="flex justify-center gap-2 mt-5">
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} title={`Rate ${s} star${s === 1 ? '' : 's'}`} onClick={() => setRating(s)}>
              <Star
                size={36}
                className={s <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/30'}
              />
            </button>
          ))}
        </div>
        {rating > 0 && (
          <p className="text-center text-sm text-muted-foreground mt-2">{LABELS[rating]}</p>
        )}

        <textarea
          placeholder="Share your feedback (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          className="w-full mt-4 px-3 py-2.5 rounded-none border border-border bg-background text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
        />

        <div className="flex gap-3 mt-4">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button className="flex-1" onClick={handleSubmit} isLoading={isSubmitting} disabled={rating === 0 || isSubmitting}>
            Submit
          </Button>
        </div>
        {submitError && (
          <p className="text-xs text-destructive text-center mt-2">{submitError}</p>
        )}
      </div>
    </div>
  )
}

// ---- main -------------------------------------------------------------------
export default function AppointmentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { patient } = usePatientAuth()
  const queryClient = useQueryClient()

  const [showMedInfo, setShowMedInfo] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<'cancel' | 'complete' | 'restart-chat' | null>(null)
  const [statusModal, setStatusModal] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
  } | null>(null)
  const [reviewSubmitted, setReviewSubmitted] = useState(false)
  const [reviewError, setReviewError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [isDownloadingResult, setIsDownloadingResult] = useState(false)

  const detailQuery = useQuery({
    queryKey: queryKeys.patient.appointmentDetail(patient?.id, id),
    enabled: !!patient?.id && !!id,
    queryFn: async () => {
      const appointment = await appointmentService.getAppointmentById(id!)
      if (!appointment) return null

      if (appointment.service_type === 'Chat Consultation') {
        return {
          appointment,
          provider: null as Provider | null,
          medicalInfo: null as AppointmentMedicalInfo | null,
          visitResult: null as VisitResult | null,
          providerRating: 0,
          alreadyReviewed: false,
        }
      }

      const normalizedAppointmentStatus = normalizeStatus(appointment.status)
      const [provider, medicalInfo, visitResult, ratingStats, alreadyReviewed] = await Promise.all([
        providerService.getProviderById(appointment.provider_id),
        appointmentService.getAppointmentMedicalInfo(appointment.id),
        normalizedAppointmentStatus === 'completed' ? appointmentService.getVisitResult(appointment.id) : Promise.resolve(null),
        providerService.getProviderRatingStats(appointment.provider_id),
        normalizedAppointmentStatus === 'completed' ? reviewService.hasReviewedAppointment(appointment.id) : Promise.resolve(false),
      ])

      return {
        appointment,
        provider,
        medicalInfo,
        visitResult,
        providerRating: ratingStats.avgRating,
        alreadyReviewed,
      }
    },
  })

  const apt = detailQuery.data?.appointment ?? null
  const provider = detailQuery.data?.provider ?? null
  const medicalInfo = detailQuery.data?.medicalInfo ?? null
  const visitResult = detailQuery.data?.visitResult ?? null
  const providerRating = detailQuery.data?.providerRating ?? 0
  const isLoading = detailQuery.isPending

  useEffect(() => {
    if (!apt || apt.service_type !== 'Chat Consultation') return

    navigate(`/patient/chat/${apt.id}`, {
      replace: true,
      state: {
        backPath: (location.state as { backPath?: string } | null)?.backPath ?? '/patient/appointments',
      },
    })
  }, [apt, navigate, location.state])

  useEffect(() => {
    if (detailQuery.data?.alreadyReviewed) {
      setReviewSubmitted(true)
    }
  }, [detailQuery.data?.alreadyReviewed])

  // --- actions ---
  const handleCancel = async () => {
    if (!apt) return
    setIsUpdating(true)
    setActionError(null)
    const result = await appointmentService.cancelAppointmentWithRefund(apt.id)
    setIsUpdating(false)
    setPendingAction(null)
    if (result.success) {
      queryClient.setQueryData(queryKeys.patient.appointmentDetail(patient?.id, apt.id), (current: typeof detailQuery.data) =>
        current ? { ...current, appointment: { ...current.appointment, status: 'cancelled' } } : current
      )
      await queryClient.invalidateQueries({ queryKey: queryKeys.patient.appointmentPages(patient?.id) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.patient.appointments(patient?.id) })
      setStatusModal({
        type: 'success',
        title: 'Appointment Cancelled',
        message: (result.refundAmount ?? 0) > 0
          ? `${formatCurrency(result.refundAmount!)} has been refunded to your wallet.`
          : 'Your appointment has been cancelled successfully.',
      })
    } else {
      const message = result.error ?? 'Failed to cancel appointment.'
      setActionError(message)
      setStatusModal({
        type: 'error',
        title: 'Cancellation Failed',
        message,
      })
    }
  }

  const handleMarkCompleted = async () => {
    if (!apt) return
    setIsUpdating(true)
    setActionError(null)
    const result = await appointmentService.updateAppointmentStatus(apt.id, 'completed')
    setIsUpdating(false)
    setPendingAction(null)
    if (result.success) {
      queryClient.setQueryData(queryKeys.patient.appointmentDetail(patient?.id, apt.id), (current: typeof detailQuery.data) =>
        current ? { ...current, appointment: { ...current.appointment, status: 'completed' } } : current
      )
      await queryClient.invalidateQueries({ queryKey: queryKeys.patient.appointmentPages(patient?.id) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.patient.appointments(patient?.id) })
      await detailQuery.refetch()
    } else {
      setActionError(result.error ?? 'Failed to update status.')
    }
  }

  const handleReviewSubmit = async (rating: number, comment: string) => {
    if (!apt || !provider) return
    setReviewError(null)
    const result = await reviewService.createReview({
      providerId: provider.id,
      appointmentId: apt.id,
      rating,
      comment: comment || undefined,
      service: apt.service_type,
    })
    if (result.success) {
      setShowReviewModal(false)
      setReviewSubmitted(true)
      await detailQuery.refetch()
    } else {
      setReviewError(result.error ?? 'Failed to submit review.')
      // If duplicate, close modal and mark as submitted
      if (result.error?.includes('already reviewed')) {
        setShowReviewModal(false)
        setReviewSubmitted(true)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 animate-pulse">
        <div className="h-6 w-16 bg-muted rounded" />
        <div className="h-32 bg-muted rounded-xl" />
        <div className="h-48 bg-muted rounded-xl" />
      </div>
    )
  }

  if (apt?.service_type === 'Chat Consultation') {
    return (
      <div className="p-4 sm:p-6 text-center py-20">
        <p className="text-muted-foreground">Redirecting to chat...</p>
      </div>
    )
  }

  if (!apt) {
    return (
      <div className="p-4 sm:p-6 text-center py-20">
        <p className="text-muted-foreground">Appointment not found.</p>
        <Button className="mt-4" onClick={() => navigate('/patient/appointments')}>Back</Button>
      </div>
    )
  }

  const meta = SERVICE_META[apt.service_type] ?? SERVICE_META['Doctor Visit']
  const ServiceIcon = meta.icon
  const isChat = apt.service_type === 'Chat Consultation'
  const normalizedStatus = normalizeStatus(apt.status)

  const medInfo = {
    currentSymptoms:    medicalInfo?.currentSymptoms    || 'Not provided',
    medicalHistory:     medicalInfo?.medicalHistory     || 'Not provided',
    allergies:          medicalInfo?.allergies          || 'None reported',
    currentMedications: medicalInfo?.currentMedications || 'Not provided',
    surgeryHistory:     medicalInfo?.surgeryHistory     || null,
    familyHistory:      medicalInfo?.familyHistory      || null,
    accidentRecord:     medicalInfo?.accidentRecord     || null,
    bloodType:          medicalInfo?.bloodType          || null,
    heightCm:           medicalInfo?.heightCm           ?? null,
    weightKg:           medicalInfo?.weightKg           ?? null,
  }

  const durationText = apt.duration === 'daily'
    ? `${apt.number_of_days ?? 1} Days`
    : `${apt.number_of_days ?? 1} Day`

  const handleCallProvider = () => {
    if (!provider?.phone_number) return
    window.location.href = `tel:${provider.phone_number}`
  }

  const handleRestartChatSession = () => {
    if (!provider?.id || !apt) return
    setPendingAction(null)
    navigate(`/patient/book/${provider.id}?service=${encodeURIComponent('Chat Consultation')}&continuationOf=${encodeURIComponent(apt.id)}`)
  }

  const getPendingActionConfig = () => {
    if (!pendingAction) return null

    if (pendingAction === 'cancel') {
      return {
        title: 'Cancel appointment?',
        message: `Cancel your appointment with ${provider?.name ?? 'this provider'}? Any eligible refund will be applied to your wallet.`,
        confirmLabel: 'Cancel Appointment',
        confirmVariant: 'destructive' as const,
        onConfirm: handleCancel,
      }
    }

    if (pendingAction === 'complete') {
      return {
        title: 'Mark appointment as completed?',
        message: `Confirm that your appointment with ${provider?.name ?? 'this provider'} has been completed.`,
        confirmLabel: 'Mark Completed',
        confirmVariant: 'primary' as const,
        onConfirm: handleMarkCompleted,
      }
    }

    return {
      title: 'Restart chat consultation?',
      message: `You will send a new chat consultation request to ${provider?.name ?? 'this provider'}. Continue?`,
      confirmLabel: 'Continue',
      confirmVariant: 'primary' as const,
      onConfirm: handleRestartChatSession,
    }
  }

  const pendingActionConfig = getPendingActionConfig()

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Back */}
      <button
        onClick={() => navigate('/patient/appointments')}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft size={16} /> Appointments
      </button>

      {/* Provider Card */}
      <div className="bg-card border border-border border p-5">
        <div className="flex items-start gap-4">
          <div className={clsx('w-14 h-14 rounded-xl flex items-center justify-center shrink-0 overflow-hidden', meta.bg)}>
            {provider?.profile_image_url ? (
              <img src={provider.profile_image_url} alt="" className="w-full h-full object-cover" />
            ) : provider?.name ? (
              <span className={clsx('font-bold text-lg', meta.textColor)}>{getInitials(provider.name)}</span>
            ) : (
              <ServiceIcon size={22} className={meta.textColor} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-foreground">{provider?.name ?? 'Unknown Provider'}</h1>
              {provider?.is_verified && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                  <Shield size={10} /> Verified
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{provider?.specialty ?? '—'}</p>
            {providerRating > 0 && (
              <div className="mt-2 inline-flex items-center gap-1 text-sm text-muted-foreground">
                <Star size={14} className="fill-yellow-400 text-yellow-400" />
                {providerRating.toFixed(1)} rating
              </div>
            )}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className={clsx('inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium', meta.bg, meta.textColor)}>
                <ServiceIcon size={12} /> {meta.label}
              </span>
              <span className={clsx('text-xs px-2.5 py-1 rounded-full font-medium capitalize', STATUS_STYLE[normalizedStatus] ?? 'bg-muted text-muted-foreground')}>
                {apt.status}
              </span>
            </div>
          </div>
        </div>

        {(provider?.qualification || provider?.experience != null || (provider?.languages && provider.languages.length > 0)) && (
          <div className="mt-5 grid gap-3 border-t border-border pt-4 sm:grid-cols-3">
            {provider?.qualification && (
              <div className="flex items-start gap-2.5">
                <Award size={15} className="mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Qualification</p>
                  <p className="text-sm text-foreground">{provider.qualification}</p>
                </div>
              </div>
            )}
            {(provider?.experience != null || provider?.years_of_experience != null) && (
              <div className="flex items-start gap-2.5">
                <BookOpen size={15} className="mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Experience</p>
                  <p className="text-sm text-foreground">{provider.experience ?? provider.years_of_experience} Years</p>
                </div>
              </div>
            )}
            {provider?.languages && provider.languages.length > 0 && (
              <div className="flex items-start gap-2.5">
                <Languages size={15} className="mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Languages</p>
                  <p className="text-sm text-foreground">{provider.languages.join(', ')}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {(provider?.about ?? provider?.bio) && (
          <div className="mt-5 border-t border-border pt-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">About</p>
            <p className="mt-1.5 text-sm leading-6 text-foreground">{provider.about ?? provider.bio}</p>
          </div>
        )}

        {provider?.specializations && provider.specializations.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Specializations</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {provider.specializations.map((specialization) => (
                <span key={specialization} className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground">
                  {specialization}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Appointment Details */}
      <div className="bg-card border border-border border p-5">
        <h2 className="font-semibold text-foreground mb-2">Appointment Details</h2>
        <div className="divide-y divide-border">
          <DetailRow icon={FileText} label="Service" value={meta.label} />
          <DetailRow
            icon={Calendar}
            label="Date"
            value={new Date(apt.scheduled_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          />
          {apt.scheduled_time && (
            <DetailRow icon={Clock} label="Time" value={formatTime12h(apt.scheduled_time)} />
          )}
          {apt.location && (
            <DetailRow icon={MapPin} label="Location" value={apt.location} />
          )}
          {apt.duration && (
            <DetailRow
              icon={Clock}
              label="Duration"
              value={durationText}
            />
          )}
          {apt.number_of_patients != null && (
            <DetailRow icon={Users} label="Number of Patients" value={String(apt.number_of_patients)} />
          )}
          {provider?.phone_number && (
            <DetailRow icon={Phone} label="Provider Phone" value={provider.phone_number} />
          )}
          <DetailRow
            icon={FileText}
            label="Cost"
            value={formatCurrency(apt.total_cost ?? 0)}
          />
          {apt.urgency_level && (
            <div className="flex items-start gap-3 py-2.5">
              <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <AlertTriangle size={15} className="text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Priority</p>
                <p className={clsx('text-sm font-medium mt-0.5', URGENCY_LABEL[apt.urgency_level]?.color ?? 'text-foreground')}>
                  {URGENCY_LABEL[apt.urgency_level]?.label ?? apt.urgency_level}
                </p>
              </div>
            </div>
          )}
          {apt.notes && (
            <DetailRow icon={FileText} label="Notes" value={apt.notes} />
          )}
        </div>
      </div>

      {/* Medical Info */}
      {medicalInfo && (
        <div className="bg-card border border-border border overflow-hidden">
          <button
            title="Toggle medical information"
            onClick={() => setShowMedInfo(!showMedInfo)}
            className="w-full flex items-center justify-between p-5"
          >
            <h2 className="font-semibold text-foreground">Medical Information</h2>
            {showMedInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {showMedInfo && (
            <div className="px-5 pb-5 space-y-3 border-t border-border pt-4">
              {medInfo.currentSymptoms && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Symptoms</p>
                  <p className="text-sm text-foreground mt-0.5">{medInfo.currentSymptoms}</p>
                </div>
              )}
              {medInfo.medicalHistory && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Medical History</p>
                  <p className="text-sm text-foreground mt-0.5">{medInfo.medicalHistory}</p>
                </div>
              )}
              {medInfo.allergies && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Allergies</p>
                  <p className="text-sm text-foreground mt-0.5">{medInfo.allergies}</p>
                </div>
              )}
              {medInfo.currentMedications && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Current Medications</p>
                  <p className="text-sm text-foreground mt-0.5">{medInfo.currentMedications}</p>
                </div>
              )}
              {medInfo.surgeryHistory && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Surgery History</p>
                  <p className="text-sm text-foreground mt-0.5">{medInfo.surgeryHistory}</p>
                </div>
              )}
              {medInfo.familyHistory && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Family Medical History</p>
                  <p className="text-sm text-foreground mt-0.5">{medInfo.familyHistory}</p>
                </div>
              )}
              {medInfo.accidentRecord && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Accident Record</p>
                  <p className="text-sm text-foreground mt-0.5">{medInfo.accidentRecord}</p>
                </div>
              )}
              {(medInfo.bloodType || medInfo.heightCm || medInfo.weightKg) && (
                <div className="flex flex-wrap gap-3 pt-1">
                  {medInfo.bloodType && (
                    <div className="rounded-xl bg-muted/50 px-3 py-2">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Blood Type</p>
                      <p className="text-sm font-semibold text-foreground mt-0.5">{medInfo.bloodType}</p>
                    </div>
                  )}
                  {medInfo.heightCm && (
                    <div className="rounded-xl bg-muted/50 px-3 py-2">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Height</p>
                      <p className="text-sm font-semibold text-foreground mt-0.5">{medInfo.heightCm} cm</p>
                    </div>
                  )}
                  {medInfo.weightKg && (
                    <div className="rounded-xl bg-muted/50 px-3 py-2">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Weight</p>
                      <p className="text-sm font-semibold text-foreground mt-0.5">{medInfo.weightKg} kg</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {normalizedStatus === 'completed' && visitResult && (
        <div className="bg-card border border-border rounded-none p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold text-foreground">Clinical Result</h2>
            <Button
              variant="outline"
              size="sm"
              isLoading={isDownloadingResult}
              onClick={async () => {
                if (!apt || !patient) return
                setIsDownloadingResult(true)
                try {
                  await downloadVisitResultPdf({ appointment: apt, result: visitResult, patient, provider })
                } finally {
                  setIsDownloadingResult(false)
                }
              }}
            >
              <Download size={14} className="mr-1.5" /> Download
            </Button>
          </div>
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Diagnosis</p>
            <p className="mt-2 text-sm leading-6 text-foreground whitespace-pre-wrap">{visitResult.diagnosisText}</p>
          </div>
          {visitResult.prescriptions.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prescription</p>
              <div className="mt-2 space-y-2">
                {visitResult.prescriptions.map((prescription) => (
                  <div key={prescription.id} className="rounded-none bg-muted/50 px-4 py-3">
                    <p className="text-sm font-medium text-foreground">{prescription.drugName}</p>
                    <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">{prescription.dosageInstructions}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {visitResult.providerSignature && (
            <div className="mt-4 rounded-none bg-muted/50 px-4 py-3">
              <p className="text-xs font-medium text-muted-foreground">Signed by</p>
              <p className="mt-0.5 text-sm font-medium text-foreground">{visitResult.providerSignature}</p>
            </div>
          )}
          {visitResult.createdAt && (
            <p className="mt-3 text-xs text-muted-foreground">
              {new Date(visitResult.createdAt).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {apt.continuation_of_appointment_id && (
        <div className="bg-card border border-border rounded-2xl p-5">
          <h2 className="font-semibold text-foreground">Consultation History</h2>
          <p className="mt-2 text-sm text-muted-foreground">This request continues a previous paid chat consultation.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(`/patient/appointments/${apt.continuation_of_appointment_id}`)}
          >
            View previous chat appointment
          </Button>
        </div>
      )}

      {/* Review section (completed) */}
      {normalizedStatus === 'completed' && !isChat && (
        <div className="bg-card border border-border rounded-none p-5">
          <h2 className="font-semibold text-foreground mb-3">Leave a Review</h2>
          {reviewSubmitted ? (
            <div className="flex items-center gap-2 text-sm text-emerald-600">
              <CheckCircle size={16} fill="currentColor" /> Thank you for your feedback!
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-3">
                How was your appointment with {provider?.name ?? 'this provider'}?
              </p>
              <Button variant="outline" size="sm" onClick={() => setShowReviewModal(true)}>
                <Star size={14} className="mr-1.5" /> Rate Experience
              </Button>
            </>
          )}
        </div>
      )}

      {/* Error */}
      {actionError && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-xl text-sm text-destructive">
          <AlertTriangle size={15} /> {actionError}
        </div>
      )}

      {/* Action buttons */}
      <div className="space-y-2">
        {(normalizedStatus === 'scheduled' || normalizedStatus === 'requested') && provider?.phone_number && (
          <Button
            className="w-full"
            onClick={handleCallProvider}
          >
            <Phone size={16} className="mr-2" /> Call Provider
          </Button>
        )}

        {isChat && (normalizedStatus === 'scheduled' || normalizedStatus === 'requested') && (
          <Button
            variant="outline"
            className="w-full border-sky-500 text-sky-600 hover:bg-sky-50"
            onClick={() => navigate(`/patient/chat/${apt.id}`, { state: { backPath: '/patient/appointments' } })}
          >
            <MessageCircle size={16} className="mr-2" /> Open Chat
          </Button>
        )}

        {normalizedStatus === 'scheduled' && (
          <Button
            variant="outline"
            className="w-full border-emerald-500 text-emerald-600 hover:bg-emerald-50"
            isLoading={isUpdating}
            onClick={() => setPendingAction('complete')}
          >
            <CheckCircle size={16} className="mr-2" /> Mark as Completed
          </Button>
        )}

        {normalizedStatus === 'requested' && (
          <>
            <div className="flex items-start gap-2 rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
              <Clock size={16} className="mt-0.5 shrink-0" />
              <p>
                Your appointment request has been sent to {provider?.name ?? 'the provider'}. You will receive a notification once they respond.
              </p>
            </div>

            <Button
              variant="destructive"
              className="w-full"
              isLoading={isUpdating}
              onClick={() => setPendingAction('cancel')}
            >
              <X size={16} className="mr-2" /> Cancel Request
            </Button>
          </>
        )}

        {normalizedStatus === 'completed' && isChat && (
          <Button
            className="w-full"
            onClick={() => setPendingAction('restart-chat')}
          >
            <MessageCircle size={16} className="mr-2" /> Restart Chat Session
          </Button>
        )}

        {normalizedStatus === 'cancelled' && (
          <>
            <div className="flex items-start gap-2 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <X size={16} className="mt-0.5 shrink-0" />
              <p>This appointment was cancelled. You can book a new appointment anytime.</p>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/patient/dashboard')}
            >
              Book New Appointment
            </Button>
          </>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && provider && (
        <ReviewModal
          providerName={provider.name}
          onSubmit={handleReviewSubmit}
          onClose={() => setShowReviewModal(false)}
          submitError={reviewError}
        />
      )}

      {pendingActionConfig && (
        <ConfirmationModal
          title={pendingActionConfig.title}
          message={pendingActionConfig.message}
          confirmLabel={pendingActionConfig.confirmLabel}
          confirmVariant={pendingActionConfig.confirmVariant}
          isLoading={isUpdating}
          onCancel={() => setPendingAction(null)}
          onConfirm={pendingActionConfig.onConfirm}
        />
      )}

      {statusModal && (
        <StatusModal
          type={statusModal.type}
          title={statusModal.title}
          message={statusModal.message}
          onPrimary={() => setStatusModal(null)}
        />
      )}
    </div>
  )
}
